import {useCallback, useMemo} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {endpoints} from '@/api/endpoints'
import {ExecutionEventType, JOB_EVENT_TYPES} from '@/types/background-jobs'
import type {ExecutionStreamEvent, JobExecution} from '@/types/background-jobs'
import {useEventSourceConnection} from './use-event-source-connection'

interface UseJobExecutionsStreamOptions {
    jobId: string
    enabled?: boolean
    onExecutionCompleted?: (executionId: string) => void
    onExecutionFailed?: (executionId: string, errorMessage: string | null) => void
}

interface UseJobExecutionsStreamResult {
    isConnected: boolean
    error: string | null
    disconnect: () => void
}

/**
 * Hook for subscribing to SSE events for a specific job's executions.
 * Updates React Query cache with real-time progress for the job's executions list.
 */
export function useJobExecutionsStream({
                                           jobId,
                                           enabled = true,
                                           onExecutionCompleted,
                                           onExecutionFailed,
                                       }: UseJobExecutionsStreamOptions): UseJobExecutionsStreamResult {
    const queryClient = useQueryClient()

    const url = useMemo(
        () => endpoints.executions.streamForJob(jobId),
        [jobId]
    )

    return useEventSourceConnection({
        url,
        enabled: enabled && !!jobId,
        eventTypes: JOB_EVENT_TYPES,
        onEvent: useCallback((raw: unknown) => {
            const data = raw as ExecutionStreamEvent
            const {executionId, eventType, status, progress} = data

            // Update execution in job-specific executions list cache. Returning
            // `old` from the updater is a no-op write; the unknown-executionId
            // branch tracks whether the current cache lacks the row so we can
            // schedule the invalidation AFTER setQueryData resolves — issuing
            // `invalidateQueries` from inside the updater lets react-query's
            // subsequent state write clear `isInvalidated` and any observer
            // reading the flag sees no invalidation.
            let needsInvalidate = false
            queryClient.setQueryData<{ content: JobExecution[] }>(
                // eslint-disable-next-line i18next/no-literal-string
                ['job-executions', jobId],
                (old) => {
                    if (!old?.content) return old

                    const exists = old.content.some(e => e.id === executionId)
                    if (!exists) {
                        needsInvalidate = true
                        return old
                    }

                    return {
                        ...old,
                        content: old.content.map((exec) =>
                            exec.id === executionId
                                ? {...exec, status: status as JobExecution['status'], progress: progress ?? exec.progress}
                                : exec
                        ),
                    }
                }
            )
            if (needsInvalidate) {
                queryClient.invalidateQueries({queryKey: ['job-executions', jobId]})
            }

            // Handle terminal events
            if (eventType === ExecutionEventType.JOB_COMPLETED || eventType === ExecutionEventType.JOB_FAILED || eventType === ExecutionEventType.JOB_CANCELLED) {
                queryClient.invalidateQueries({queryKey: ['job-executions', jobId]})
                queryClient.invalidateQueries({queryKey: ['job', jobId]})
            }
            if (eventType === ExecutionEventType.JOB_COMPLETED) {
                onExecutionCompleted?.(executionId)
            } else if (eventType === ExecutionEventType.JOB_FAILED) {
                onExecutionFailed?.(executionId, data.errorMessage ?? null)
            }
        }, [jobId, queryClient, onExecutionCompleted, onExecutionFailed]),
    })
}
